import {cancelRowValueVolSensor, getRowValueVolSensor} from '@/api/volumeSensorApi';
import {FIELD_NAME} from '@/constants/pcConstAppData';

import {ChartZoomLevel, ID_CHART, VolumeName} from '../volumeSensorGraph';

export const DataType = {
  Percentage: 'Percent',
  Unit: 'Unit',
};
const ALL_DATA = -999999;
const ALL_DATA_WINDOW = 200;
const defaultResponse = {
  graphData: [],
  avgConcentarionRate: 0.0,
  elapsedTime: '00:00:00',
};

/**
 * Compose the response into UI data structure
 * @param {Number} Capacity if type was unit it will stored the capacity number but if type was percentage it will determine as value in percentage
 * @param {DataType} type
 * @param {string} runningMethod
 * @returns
 */
export const GetLatestVolumeSensor = async (Capacity, type, runningMethod) => {
  // eslint-disable-next-line no-param-reassign
  if (Capacity === undefined) Capacity = 10;

  return getRowValueVolSensor()
    .then((response) => {
      const {data} = response;
      const rowValue = data?.RowValue ?? [];
      const currentDataLength = rowValue.length;

      let initData = ['x', VolumeName.Sample, VolumeName.Buffer, VolumeName.FinalVolume];
      let removeDataIndex = -1;
      switch (runningMethod) {
        case FIELD_NAME.CONCENTRATE:
          removeDataIndex = initData.indexOf(VolumeName.Buffer);
          initData = ['x', VolumeName.Sample, VolumeName.FinalVolume];
          rowValue.forEach((e) => e.splice(removeDataIndex, 1));
          break;
        case FIELD_NAME.CONC_TWO_TUBES:
          initData = ['x', VolumeName.Tube1, VolumeName.Tube2, VolumeName.FinalVolume];
          break;
        case FIELD_NAME.BUFFER_EXCHANGE:
          initData = ['x', VolumeName.Sample, VolumeName.Buffer, VolumeName.FinalVolume];

          break;
        default:
          break;
      }

      /**
       * Converting the capacity value
       * if the type was percentage it will converted into unit value
       */
      if (type === DataType.Percentage) {
        const dataLength = Math.round((Capacity / 100) * currentDataLength);
        // eslint-disable-next-line no-param-reassign
        Capacity = dataLength < 10 ? 10 : dataLength;
        // eslint-disable-next-line no-param-reassign
      } else if (Capacity === ALL_DATA) Capacity = ALL_DATA_WINDOW;

      if (currentDataLength > 0) {
        const graphData = [initData];
        let filteredData = null;

        if (currentDataLength >= Capacity) filteredData = rowValue.slice(currentDataLength - Capacity, undefined);
        else {
          const emptyData = [0, ...rowValue[currentDataLength].map((e) => e)];
          filteredData = [...new Array(Capacity - currentDataLength).fill(emptyData), ...rowValue];
        }

        graphData.push(...filteredData);

        return {
          graphData,
          avgConcentarionRate: data.AvgConcentarionRate,
          elapsedTime: data.ElapsedTime,
        };
      }
      return defaultResponse;
    })
    .catch((error) => {
      console.error('[VolumeSensorDataSource] - error', error);
      return defaultResponse;
    });
};

export const cancelGetLatestVolumeSensor = () => {
  cancelRowValueVolSensor();
};

export const getVolumeSensorChart = async (runningMethod, highVolumeBuffer) => {
  let result = {};
  try {
    const response = await getRowValueVolSensor();
    const {data} = response;
    const {AvgConcentarionRate, ElapsedTime, RowValue} = data;

    result = {
      rowValue: volumeSensorResolver(RowValue, runningMethod, highVolumeBuffer),
      avgConcentarionRate: AvgConcentarionRate,
      elapsedTime: ElapsedTime,
    };
  } catch (error) {
    console.error('getVolumeSensor', error);
  }

  return result;
};

export const getVolumeSensor = async (runningMethod) => {
  let result = {};
  try {
    const response = await getRowValueVolSensor();
    const {data} = response;
    const {AvgConcentarionRate, ElapsedTime, RowValue} = data;

    rowValueSolver(RowValue, runningMethod);

    result = {
      rowValue: RowValue,
      avgConcentarionRate: AvgConcentarionRate,
      elapsedTime: ElapsedTime,
    };
  } catch (error) {
    console.error('getVolumeSensor', error);
  }

  return result;
};

export const getInitVolumeSensor = (runningMethod, highVolumeBuffer) => {
  let result = ['x', VolumeName.Sample, VolumeName.Buffer, VolumeName.FinalVolume];

  switch (runningMethod) {
    case FIELD_NAME.CONCENTRATE:
      result = ['x', VolumeName.Sample, VolumeName.FinalVolume];

      break;
    case FIELD_NAME.CONC_TWO_TUBES:
      result = ['x', VolumeName.Tube1, VolumeName.Tube2, VolumeName.FinalVolume];

      break;
    case FIELD_NAME.BUFFER_EXCHANGE:
      result = [
        'x',
        VolumeName.Sample,
        (VolumeName.Buffer = !highVolumeBuffer ? VolumeName.Buffer : VolumeName.EstCurrentBuffer),
        VolumeName.FinalVolume,
      ];

      break;
    default:
      break;
  }

  return result;
};

export const rowValueSolver = (data, runningMethod) => {
  const initData = ['x', VolumeName.Sample, VolumeName.Buffer, VolumeName.FinalVolume];
  let removeDataIndex = -1;

  if (runningMethod === FIELD_NAME.CONCENTRATE) {
    removeDataIndex = initData.indexOf(VolumeName.Buffer);
    data.forEach((e) => e.splice(removeDataIndex, 1));
  }

  data.forEach((entry) => {
    const finalVolumeIndex = entry.length - 1;
    if (finalVolumeIndex !== -1 && entry[finalVolumeIndex] !== undefined) {
      entry[finalVolumeIndex] = Math.floor(entry[finalVolumeIndex] * 100) / 100;
    }
  });
};

export const volumeSensorZoom = (data, zoomLevel) => {
  const totalRecords = data.length;
  const recordsToShow = Math.ceil((totalRecords * (10 - zoomLevel + 1)) / 10);

  return data.slice(totalRecords - recordsToShow);
};

export const volumeSensorResolver = (
  data,
  runningMethod,
  highVolumeBuffer = false,
  useInitDataResolver = true,
  zoomLevel = ChartZoomLevel.MINIMUM_ZOOM_LEVEL,
) => {
  // eslint-disable-next-line no-param-reassign
  data = volumeSensorZoom(data, zoomLevel);

  if (useInitDataResolver) rowValueSolver(data, runningMethod);

  if (useInitDataResolver)
    return generateChartParam([getInitVolumeSensor(runningMethod, highVolumeBuffer), ...data], runningMethod);
  return generateChartParam([...data], runningMethod);
};

export const generateChartParam = (data, runningMethod) => {
  const colors = {
    [VolumeName.Sample]: '#F7A246',
    [VolumeName.Buffer]: '#215eff',
    [VolumeName.FinalVolume]: 'red',
    [VolumeName.Tube1]: '#F7A246',
    [VolumeName.Tube2]: '#215eff',
  };

  let names = {
    [VolumeName.FinalVolume]: [VolumeName.FinalVolume],
  };

  let lineClasses = ['sample', 'buffer', 'finalVolume'];

  switch (runningMethod) {
    case FIELD_NAME.CONCENTRATE:
      names = {
        ...names,
        [VolumeName.Sample]: [VolumeName.Sample],
      };
      lineClasses = lineClasses.filter((e) => e !== 'buffer');
      delete colors[VolumeName.Buffer];
      break;
    case FIELD_NAME.CONC_TWO_TUBES:
      names = {
        ...names,
        [VolumeName.Sample]: [VolumeName.Tube1],
        [VolumeName.Buffer]: [VolumeName.Tube2],
      };
      break;
    case FIELD_NAME.BUFFER_EXCHANGE:
      names = {
        ...names,
        [VolumeName.Sample]: [VolumeName.Sample],
        [VolumeName.Buffer]: [VolumeName.Buffer],
      };
      break;
    default:
      break;
  }

  const chartParams = {
    size: {
      height: '430',
    },
    data: {
      x: 'x',
      rows: data,
      type: 'line',
      // eslint-disable-next-line no-unused-vars
      onclick: (d, element) => {},
      colors,
      names,
    },
    line: {
      classes: lineClasses,
    },
    bindto: ID_CHART,
    point: {
      show: false,
      pattern: ['-------  '],
    },
    axis: {
      y: {
        label: {
          text: 'Volume (mL)',
          position: 'outer-middle',
        },
        padding: {
          bottom: 16,
        },
      },
      x: {
        height: 55,
        type: 'timeseries',
        label: {
          text: 'Time (HH:MM:SS)',
          position: 'outer-center',
        },
        tick: {
          count: 5,
          format: '%H:%M:%S',
          culling: false,
        },
        localtime: false,
      },
    },
    legend: {
      contents: {
        bindto: '#legend',
        template: "<div  style='color:{=COLOR}'>-------  {=TITLE}</div>",
      },
      item: {
        onclick: () => false,
        onover: () => false,
      },
    },
    grid: {
      x: {
        show: true,
      },
      y: {
        show: true,
      },
    },
    // zoom: {
    //   enabled: true,
    // },
  };

  return chartParams;
};

export const getLatestRowValueData = (data = []) => {
  if (!data.length) return [];
  // Step 1: Collect all unique timestamps
  const result = data[0].values.map((e) => {
    // Convert timestamp to milliseconds
    const timeInMs = new Date(e.x).getTime();

    const valuesAtTimestamp = data.map((d) => {
      const ss = d.values.find((v) => new Date(v.x).getTime() === timeInMs);

      return ss?.value;
    });

    return [timeInMs, ...valuesAtTimestamp];
  });

  return result;
};

export function truncate(value, precision) {
  const factor = 10 ** precision;
  return Math.trunc(value * factor) / factor;
}

export function compressData(data, runningMethod, dataWindow = 100, maxDataShowWindow = 200) {
  const dataLength = data.length - 1;
  if (dataLength < maxDataShowWindow) return data;

  const windowData = Math.floor(dataLength / dataWindow);
  const newData = [];
  // let currentIndex = 0;

  for (let i = 0; i < dataLength; i++) {
    const sumOfData = runningMethod === FIELD_NAME.CONCENTRATE ? [0.0, 0.0, 0.0] : [0.0, 0.0, 0.0, 0.0];
    let j;

    for (j = i; j < i + windowData; j++) {
      const selectedData = data[j];
      sumOfData[0] = parseFloat(selectedData[0]); // time
      sumOfData[1] += parseFloat(selectedData[1]); // protein
      if (runningMethod === FIELD_NAME.CONCENTRATE) {
        sumOfData[2] = parseFloat(selectedData[2]); // totalVolume
      } else {
        sumOfData[2] += parseFloat(selectedData[2]); // buffer
        sumOfData[3] = parseFloat(selectedData[3]); // totalVolume
      }
    }

    const avgOfData =
      runningMethod === FIELD_NAME.CONCENTRATE
        ? [
            sumOfData[0], // use latest time from one batch
            truncate(sumOfData[1] / windowData, 1),
            sumOfData[2],
          ]
        : [
            sumOfData[0], // use latest time from one batch
            truncate(sumOfData[1] / windowData, 1),
            truncate(sumOfData[2] / windowData, 1),
            sumOfData[3],
          ];

    newData.push(avgOfData);
    // currentIndex++;
    i = j - 1;
  }

  return newData;
}
